import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Card, CardContent, Collapse, IconButton, Box, Typography, Drawer, Tooltip, Chip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import StrategyMiniChart from './StrategyMiniChart';
import PercentileBands from './PercentileBands';
import { formatChartData, calculatePercentile } from '../../utils/strategyCalculations';
import { fetchStrategyData, fetchStrategyDataBySymbols, fetchPercentileBands } from '../../api/dataFeedApi';
import { getStrategyById, updateStrategyParameters } from '../../api/strategyApi';
import { backendToUI, uiToBackend, mergeWithDefaults } from '../../utils/parameterConverter';
import { useAuth } from '../../provider/authProvider';
import './StrategyCard.css';

// Expandable icon animation
const ExpandMore = styled((props) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
})(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
    }),
}));

/**
 * StrategyCard Component - 3-Section Compact Design (Blueprint Section 2.1)
 *
 * SECTION 1: HEADER (48px) - 9%
 *   - Strategy name + symbol pair (left)
 *   - Total P&L (right)
 *
 * SECTION 2: ANALYTICS CHART (300px) - 55% PRIORITY
 *   - StrategyMiniChart with 4 modes (percentile, zscore, spread, session)
 *   - Mode toggle buttons
 *   - 300px fixed height
 *
 * SECTION 3: GRID STATUS (120px) - 22%
 *   - 2-column layout: Performance | Position
 *   - Collapsed by default (space saving)
 *
 * CONTROL BAR (80px) - 14%
 *   - All action buttons (Buy/Sell/Close/Reset)
 *   - Config button (opens drawer)
 *
 * Total Card Height: ~550px (vs old ~800px)
 *
 * @param {Object} props
 * @param {string} props.strategyId - Strategy ID
 * @param {string} props.strategyName - Display name
 * @param {Object} props.strategyData - Strategy data (marketData, performance, position)
 * @param {Function} props.onBuySpread - Buy spread handler
 * @param {Function} props.onSellSpread - Sell spread handler
 * @param {Function} props.onCloseBuy - Close buy handler
 * @param {Function} props.onCloseSell - Close sell handler
 * @param {Function} props.onCloseAll - Close all handler
 * @param {Function} props.onResetStrategy - Reset strategy handler
 * @param {Function} props.onApplyParameters - Apply parameters handler
 * @param {Function} props.formatWithPrecision - Number formatting utility
 * @param {number} props.realTimePnL - Real-time P&L value
 */
const StrategyCard = ({
    strategyId,
    strategyName,
    strategyData,
    onBuySpread,
    onSellSpread,
    onCloseBuy,
    onCloseSell,
    onCloseAll,
    onResetStrategy,
    onApplyParameters,
    formatWithPrecision,
    realTimePnL
}) => {
    // Collapsible section state (Both collapsed by default for professional, clean look)
    const [gridStatusExpanded, setGridStatusExpanded] = useState(false);
    const [controlsExpanded, setControlsExpanded] = useState(false); // Controls collapsed by default
    const [percentileBandsExpanded, setPercentileBandsExpanded] = useState(false); // Percentile Bands collapsed by default

    // Config Drawer state
    const [configDrawerOpen, setConfigDrawerOpen] = useState(false);

    // Auth context for API calls
    const { token } = useAuth();

    // C4: Check if strategy is disabled by admin
    const isDisabledByAdmin = strategyData?.isEnabled === false;
    const disabledTooltip = "Strategy disabled by administrator. Contact support.";

    // Original parameters state (for RESET functionality - A4)
    const [originalParams, setOriginalParams] = useState(null);

    // Loading/Saving states for CONFIG drawer
    const [isLoadingParams, setIsLoadingParams] = useState(false);
    const [isSavingParams, setIsSavingParams] = useState(false);

    // Grid parameters state (Complete 21 parameters from screenshot)
    const [strategyParams, setStrategyParams] = useState({
        // Entry Settings
        gridThreshold: '',
        tradeEntryMode: 'Percentile', // Dropdown: Percentile/Z-Score/Beta

        // Grid Configuration
        maxGridLevels: '',
        buyBaseLotSize: '',
        sellBaseLotSize: '',
        buyLotSizeMultiplier: '',
        sellLotSizeMultiplier: '',
        maxLotAllowed: '',
        buyGrid: '',
        sellGrid: '',

        // Take Profit Settings
        tpGridEnabled: false,
        tpMode: 'Averaging Sigmoid', // Dropdown
        tpType: 'Pip-based', // Dropdown: Pip-based/ATR-based
        tpGridType: '',
        buyLegTP: '',
        sellLegTP: '',
        buyLegDL: '',
        sellLegDL: '',
        accumulativeTP: '',

        // Risk Management
        stopLossEnabled: false,
        stopLossPips: '',
        accumulativeSL: '',

        // Legacy fields (for backward compatibility)
        benchmarkThresholdBuy: '',
        benchmarkThresholdSell: ''
    });

    // Collapsible sections state for config drawer
    const [configSectionsExpanded, setConfigSectionsExpanded] = useState({
        entry: true,
        grid: false,
        takeProfit: false,
        risk: false,
        advanced: false
    });

    // Chart data state (Task #6 - Analytics Integration)
    const [historicalBars, setHistoricalBars] = useState([]);
    const [chartMode, setChartMode] = useState('percentile');
    const [percentileBands, setPercentileBands] = useState(null);
    const [dailyStats, setDailyStats] = useState(null);  // NEW: Daily high/low for each indicator
    const [isLoadingChart, setIsLoadingChart] = useState(false);

    // Extract data from strategyData prop
    const marketData = useMemo(() => strategyData?.marketData || {}, [strategyData]);
    const performance = useMemo(() => strategyData?.performance || {}, [strategyData]);
    const position = useMemo(() => strategyData?.position || {}, [strategyData]);

    const primarySymbol = marketData?.futuresSymbol || marketData?.spotSymbol || '';

    // Format chart data for StrategyMiniChart (Task #6)
    const chartData = useMemo(() => {
        return formatChartData(historicalBars, chartMode, percentileBands);
    }, [historicalBars, chartMode, percentileBands]);

    // Calculate Key Metrics
    const keyMetrics = useMemo(() => {
        const betaSpread = marketData?.betaSpread ?? null;
        const percentile = (percentileBands && betaSpread !== null)
            ? calculatePercentile(betaSpread, percentileBands)
            : null;

        const zScore = marketData?.zScore ?? null;

        // Calculate volatility (standard deviation proxy)
        // Primary: Use historical bars for accurate calculation
        // Fallback: Use IQR (Interquartile Range) / 1.35 ≈ σ when no historical data
        const volatility = historicalBars.length > 0
            ? Math.sqrt(
                historicalBars.reduce((sum, bar) => {
                    const diff = bar.spread - (percentileBands?.p50 || 0);
                    return sum + (diff * diff);
                }, 0) / historicalBars.length
            )
            : percentileBands?.p75 && percentileBands?.p25
                ? (percentileBands.p75 - percentileBands.p25) / 1.35
                : null;

        return {
            betaSpread,
            percentile,
            zScore,
            volatility
        };
    }, [
        marketData?.betaSpread,
        marketData?.zScore,
        percentileBands?.p25,
        percentileBands?.p50,
        percentileBands?.p75,
        historicalBars.length
    ]);

    // Handle parameter changes
    const handleParamChange = useCallback((key, value) => {
        setStrategyParams(prev => ({
            ...prev,
            [key]: value
        }));
    }, []);

    // Save feedback state for showing warnings about non-hot-reloadable params
    const [saveMessage, setSaveMessage] = useState(null);

    // A5: Apply/Save parameters handler - Persists changes to backend
    // User has FULL control over ALL config parameters - send everything to API
    // API will return which params were updated vs which need strategy stopped
    const handleApply = useCallback(async () => {
        if (!strategyId || !token) {
            console.error('[StrategyCard] SAVE: Missing strategyId or token');
            return;
        }

        setIsSavingParams(true);
        setSaveMessage(null);

        try {
            // Convert ALL UI params to backend format - user controls everything
            const backendParams = uiToBackend(strategyParams);

            // Only call API if there are params to update
            if (Object.keys(backendParams).length === 0) {
                console.log('[StrategyCard] SAVE: No parameters to save');
                setSaveMessage({ type: 'info', text: 'No changes to save' });
                setIsSavingParams(false);
                return;
            }

            console.log('[StrategyCard] SAVE: Sending ALL params to API:', {
                strategyId,
                backendParams
            });

            // Call API to persist parameters
            const result = await updateStrategyParameters(token, strategyId, backendParams);

            console.log('[StrategyCard] SAVE: API response:', result);

            const updatedCount = result.updatedParameters?.length || 0;
            const failedCount = result.failedParameters?.length || 0;

            if (result.success || updatedCount > 0) {
                // Full or partial success - update originalParams for successfully updated ones
                setOriginalParams({ ...strategyParams });

                // Also call the legacy onApplyParameters if provided
                if (onApplyParameters) {
                    onApplyParameters(strategyId, strategyParams);
                }

                if (failedCount > 0) {
                    // Partial success - some params need strategy stopped
                    setSaveMessage({
                        type: 'warning',
                        text: `✓ Updated ${updatedCount}. ${failedCount} params need strategy stopped first.`
                    });
                    // Don't auto-close on partial success - let user see message
                    console.log('[StrategyCard] SAVE: Partial success. Failed params:', result.failedParameters);
                } else {
                    // Full success
                    setSaveMessage({
                        type: 'success',
                        text: `✓ All ${updatedCount} parameter(s) saved!`
                    });
                    // Close drawer after full success
                    setTimeout(() => {
                        setConfigDrawerOpen(false);
                        setSaveMessage(null);
                    }, 1500);
                }

                console.log('[StrategyCard] SAVE: Parameters saved. Updated:', result.updatedParameters);
            } else {
                // Complete failure
                setSaveMessage({
                    type: 'error',
                    text: result.message || 'Failed to update parameters. Try stopping the strategy first.'
                });
                console.error('[StrategyCard] SAVE: API returned error:', result);
            }
        } catch (error) {
            console.error('[StrategyCard] SAVE: Error saving parameters:', error);
            setSaveMessage({
                type: 'error',
                text: `Error: ${error.message || 'Failed to save'}`
            });
            // Don't close drawer on error - let user retry
        } finally {
            setIsSavingParams(false);
        }
    }, [strategyId, strategyParams, originalParams, token, onApplyParameters]);

    // DataFeed API Bootstrap: Fetch historical data ONCE on mount (Task #6)
    // PHASE 1: Prefer new symbol-based endpoint (data-by-symbols), fallback to old endpoint (strategy-data)
    useEffect(() => {
        const bootstrapChartData = async () => {
            // Only fetch if we have a valid symbol (futures/spot pair)
            const symbol1 = marketData?.futuresSymbol;
            const symbol2 = marketData?.spotSymbol;

            if (!symbol1 || !symbol2) {
                return;
            }

            setIsLoadingChart(true);
            try {
                // PHASE 1: Try NEW endpoint first (symbol-based, avoids "Symbol not found" errors)
                const response = await fetchStrategyDataBySymbols(
                    symbol1,   // e.g., "XAUUSD_ct"
                    symbol2,   // e.g., "XAUEUR_ct"
                    'H1',      // timeframe
                    100        // bars (100 historical bars)
                );

                // Store historical bars
                setHistoricalBars(response.data || []);

                // Store percentile bands
                if (response.percentiles) {
                    setPercentileBands(response.percentiles);
                }

                // Store dailyStats (NEW FEATURE)
                if (response.dailyStats) {
                    setDailyStats(response.dailyStats);
                }

            } catch (newEndpointError) {
                console.warn('[StrategyCard] ⚠️ NEW endpoint failed, trying OLD endpoint fallback...', newEndpointError.message);

                // FALLBACK 1: Try OLD endpoint (strategy-data with pair name)
                try {
                    const symbol = `${symbol1}/${symbol2}`;
                    const response = await fetchStrategyData(
                        symbol,
                        'H1',  // timeframe
                        100,   // bars
                        true,  // includePercentiles
                        true,  // includeZScore
                        false  // includeSpreadOnly
                    );

                    // Store historical bars
                    setHistoricalBars(response.data || []);

                    // Store percentile bands
                    if (response.percentiles) {
                        setPercentileBands(response.percentiles);
                    }

                } catch (oldEndpointError) {
                    console.error('[StrategyCard] ❌ Both endpoints failed, using marketData fallback', oldEndpointError.message);

                    // FALLBACK 2: Generate single data point from current marketData
                    // This ensures chart always has data to display even when API fails
                    if (marketData?.betaSpread !== undefined && marketData?.betaSpread !== null) {
                        const fallbackBar = {
                            timestamp: new Date().toISOString(),
                            spread: marketData.betaSpread,
                            beta: marketData.beta || null,
                            zScore: marketData.zScore || 0,
                            session: marketData.session || 'UNKNOWN'
                        };

                        setHistoricalBars([fallbackBar]);

                        // Also set percentile bands from marketData if available
                        if (marketData.upperBand && marketData.lowerBand && marketData.medianSpread) {
                            setPercentileBands({
                                p25: marketData.lowerBand,
                                p50: marketData.medianSpread,
                                p75: marketData.upperBand
                            });
                        }
                    }
                }
            } finally {
                setIsLoadingChart(false);
            }
        };

        bootstrapChartData();
    }, [marketData?.futuresSymbol, marketData?.spotSymbol]); // Run once when symbols are ready

    // SignalR Real-Time Updates: Map percentile bands from marketData (Task #6)
    // Debounced to prevent excessive re-renders (max 1 update/sec)
    useEffect(() => {
        if (!marketData || !marketData.upperBand || !marketData.lowerBand || !marketData.medianSpread) {
            return; // Percentile data not ready
        }

        const updatePercentileBands = () => {
            // Map backend percentile structure to frontend format
            const realTimePercentileBands = {
                p25: marketData.lowerBand,    // Buy threshold
                p50: marketData.medianSpread, // Median
                p75: marketData.upperBand     // Sell threshold
            };

            setPercentileBands(realTimePercentileBands);

            // Append current spread to chart (keep last 100 bars)
            if (marketData.betaSpread && historicalBars.length > 0) {
                setHistoricalBars(prev => {
                    // Check if last bar is recent (within 1 minute) to avoid duplicates
                    const lastBar = prev[prev.length - 1];
                    const now = new Date();
                    const lastBarTime = lastBar ? new Date(lastBar.timestamp) : null;

                    // Only append if last bar is >1 minute old (H1 timeframe)
                    if (!lastBarTime || (now - lastBarTime) > 60000) {
                        const newBar = {
                            timestamp: now.toISOString(),
                            spread: marketData.betaSpread,
                            beta: marketData.beta || null,
                            zScore: marketData.zScore || null,
                            session: marketData.session || 'UNKNOWN'
                        };

                        // Append new bar and keep last 100
                        return [...prev, newBar].slice(-100);
                    }

                    return prev; // No change if recent bar exists
                });
            }
        };

        // ALWAYS update immediately if percentileBands is null (fixes first card race condition)
        if (!percentileBands) {
            updatePercentileBands();
            return;
        }

        // Debounce subsequent updates to 1/sec (Validation Gate 6.3 - Performance)
        const debounceTimeout = setTimeout(updatePercentileBands, 1000);

        // Cleanup timeout on unmount (prevent memory leaks)
        return () => clearTimeout(debounceTimeout);
    }, [marketData]); // Simplified dependency - only trigger on marketData changes

    // Task 2.4: Percentile Refresh Interval (15 minutes)
    // Periodically refresh percentile bands + dailyStats from DataFeed API to account for market regime changes
    // PHASE 1: Prefer new symbol-based endpoint (includes dailyStats)
    useEffect(() => {
        if (!primarySymbol || !marketData?.futuresSymbol || !marketData?.spotSymbol) {
            return; // Symbol pair not ready
        }

        const symbol1 = marketData.futuresSymbol;
        const symbol2 = marketData.spotSymbol;
        const symbol = `${symbol1}/${symbol2}`;

        const refreshPercentiles = async () => {
            try {
                // Try NEW endpoint first (includes dailyStats)
                const response = await fetchStrategyDataBySymbols(
                    symbol1,
                    symbol2,
                    'H1',
                    1  // Only fetch 1 bar for lightweight refresh
                );

                // Update percentile bands
                if (response.percentiles) {
                    setPercentileBands(response.percentiles);
                }

                // Update dailyStats (NEW FEATURE - includes high/low for UI)
                if (response.dailyStats) {
                    setDailyStats(response.dailyStats);
                }

            } catch (newEndpointError) {
                console.warn('[StrategyCard] ⚠️ 15-min refresh: NEW endpoint failed, trying OLD endpoint...', newEndpointError.message);

                // FALLBACK: Try OLD endpoint (percentiles only, no dailyStats)
                try {
                    const bands = await fetchPercentileBands(symbol, 'H1');
                    setPercentileBands(bands);
                } catch (oldEndpointError) {
                    console.error('[StrategyCard] ❌ 15-min refresh: Both endpoints failed', oldEndpointError.message);
                    // Keep old percentiles/dailyStats on error (graceful degradation)
                }
            }
        };

        // Initial refresh on mount (optional - already loaded in bootstrapChartData)
        // refreshPercentiles();

        // Refresh every 15 minutes (900000ms)
        const interval = setInterval(refreshPercentiles, 900000);

        // Cleanup interval on unmount
        return () => clearInterval(interval);
    }, [primarySymbol, marketData?.futuresSymbol, marketData?.spotSymbol]);

    // ===================================================================
    // A3: Fetch strategy parameters when CONFIG drawer opens
    // Populates CONFIG drawer with Admin-configured values
    // ===================================================================
    useEffect(() => {
        const fetchStrategyParams = async () => {
            if (!configDrawerOpen || !strategyId || !token) {
                return; // Drawer not open or missing required data
            }

            setIsLoadingParams(true);
            try {
                console.log('[StrategyCard] Fetching strategy parameters for strategyId:', strategyId);

                // Fetch full strategy data including parameters
                const strategyData = await getStrategyById(token, strategyId);

                console.log('[StrategyCard] Strategy data received:', strategyData);

                // Extract parameters from response
                const backendParams = strategyData?.parameters || strategyData?.Parameters || {};

                // Convert backend params to UI format
                const uiParams = backendToUI(backendParams);

                // Define default values for UI fields
                const defaultParams = {
                    gridThreshold: '',
                    tradeEntryMode: 'Percentile',
                    maxGridLevels: '',
                    buyBaseLotSize: '',
                    sellBaseLotSize: '',
                    buyLotSizeMultiplier: '',
                    sellLotSizeMultiplier: '',
                    maxLotAllowed: '',
                    buyGrid: '',
                    sellGrid: '',
                    tpGridEnabled: false,
                    tpMode: 'Averaging Sigmoid',
                    tpType: 'Pip-based',
                    tpGridType: '',
                    buyLegTP: '',
                    sellLegTP: '',
                    buyLegDL: '',
                    sellLegDL: '',
                    accumulativeTP: '',
                    stopLossEnabled: false,
                    stopLossPips: '',
                    accumulativeSL: '',
                    benchmarkThresholdBuy: '',
                    benchmarkThresholdSell: ''
                };

                // Merge fetched params with defaults (ensures all fields have values)
                const mergedParams = mergeWithDefaults(uiParams, defaultParams);

                console.log('[StrategyCard] Merged params for UI:', mergedParams);

                // Set current params state
                setStrategyParams(mergedParams);

                // Store original params for RESET functionality (A4)
                setOriginalParams(mergedParams);

            } catch (error) {
                console.error('[StrategyCard] Error fetching strategy parameters:', error);
                // Keep current params on error (graceful degradation)
            } finally {
                setIsLoadingParams(false);
            }
        };

        fetchStrategyParams();
    }, [configDrawerOpen, strategyId, token]);

    return (
        <Card sx={{
            backgroundColor: '#0a0a0a',
            border: '1px solid #1a1a1a',
            borderLeft: isDisabledByAdmin ? '2px solid #6b7280' : '2px solid #991b1b', // C4: Gray when disabled
            borderRadius: '4px',
            overflow: 'hidden',
            height: 'auto',
            opacity: isDisabledByAdmin ? 0.6 : 1, // C4: Reduced opacity when disabled
            filter: isDisabledByAdmin ? 'grayscale(30%)' : 'none', // C4: Slight grayscale when disabled
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 0 12px rgba(153, 27, 27, 0.08), 0 4px 12px rgba(0, 0, 0, 0.8)', // Softer glow
            position: 'relative'
        }}>
            {/* ===================================================================
                SECTION 1: HEADER (48px) - 9%
                Compact 2-row header with strategy info and P&L
                =================================================================== */}
            <Box sx={{
                background: '#0a0a0a',
                borderBottom: '1px solid #1a1a1a',
                padding: '6px 14px',
                height: '40px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                {/* Left: Strategy Name + Symbol Pair */}
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Typography sx={{
                            color: isDisabledByAdmin ? '#6b7280' : '#ffffff',
                            fontSize: '13px',
                            fontWeight: '900',
                            letterSpacing: '0.5px',
                            lineHeight: '1.2',
                            fontFamily: 'Arial, sans-serif'
                        }}>
                            {strategyName || `Strategy #${strategyId}`}
                        </Typography>
                        {/* C5: DISABLED BY ADMIN badge */}
                        {isDisabledByAdmin && (
                            <Chip
                                label="DISABLED"
                                size="small"
                                sx={{
                                    height: '16px',
                                    fontSize: '8px',
                                    fontWeight: 700,
                                    backgroundColor: '#dc2626',
                                    color: '#ffffff',
                                    '& .MuiChip-label': {
                                        padding: '0 6px'
                                    }
                                }}
                            />
                        )}
                    </Box>
                    <Typography sx={{
                        color: '#6b7280',
                        fontSize: '10px',
                        fontFamily: 'Courier New, monospace',
                        fontWeight: 600,
                        lineHeight: '1.2',
                        letterSpacing: '0.3px'
                    }}>
                        {marketData?.futuresSymbol || '-'} / {marketData?.spotSymbol || '-'}
                    </Typography>
                </Box>

                {/* Right: Total P&L */}
                <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{
                        color: '#6b7280',
                        fontSize: '9px',
                        lineHeight: '1.2',
                        marginBottom: '2px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        Total P&L
                    </Typography>
                    <Typography sx={{
                        color: realTimePnL >= 0 ? '#22c55e' : '#ef4444',
                        fontSize: '15px',
                        fontWeight: 900,
                        fontFamily: 'Courier New, monospace',
                        lineHeight: '1.2',
                        letterSpacing: '0.3px'
                    }}>
                        {realTimePnL !== undefined ? `${realTimePnL >= 0 ? '+$' : '-$'}${Math.abs(realTimePnL).toFixed(2)}` : '-'}
                    </Typography>
                </Box>
            </Box>

            {/* Card Content Container - ULTRA-SLEEK */}
            <CardContent sx={{
                flex: 1,
                padding: '6px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                '&:last-child': {
                    paddingBottom: '6px'
                }
            }}>
                {/* ===================================================================
                    KEY METRICS GRID (4-column) - INSTITUTIONAL GRADE
                    Beta Spread | Percentile | Z-Score | Volatility
                    Each card shows: Current Value + 24H High/Low
                    =================================================================== */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '4px'
                }}>
                    {/* Beta Spread Metric */}
                    <Box sx={{
                        backgroundColor: '#141414',
                        border: '1px solid #262626',
                        borderRadius: '4px',
                        padding: '5px 7px',
                        textAlign: 'center',
                        overflow: 'hidden',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                        height: '65px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                            <Typography sx={{
                                color: 'rgba(156, 163, 175, 0.7)',
                                fontSize: '8px',
                                fontWeight: '600',
                                letterSpacing: '0.5px',
                                marginBottom: '2px',
                                lineHeight: '1.2',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere'
                            }}>
                                BETA SPREAD
                            </Typography>
                            <Typography sx={{
                                color: '#ffffff',
                                fontSize: '14px',
                                fontWeight: '900',
                                fontFamily: 'Courier New, monospace',
                                lineHeight: '1.1',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere'
                            }}>
                                {keyMetrics.betaSpread !== null
                                    ? formatWithPrecision(keyMetrics.betaSpread, primarySymbol)
                                    : '-'}
                            </Typography>
                        </Box>
                        {/* 24H High/Low - PHASE 2: Use dailyStats (actual daily extremes) */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingTop: '4px', borderTop: '1px solid #262626', width: '100%' }}>
                            <Box sx={{ flex: '1', minWidth: '0' }}>
                                <Typography sx={{ color: 'rgba(34, 197, 94, 0.9)', fontSize: '11px', lineHeight: '1', fontWeight: '800' }}>H</Typography>
                                <Typography sx={{
                                    color: 'rgba(34, 197, 94, 1)',
                                    fontSize: '12px',
                                    fontWeight: '800',
                                    fontFamily: 'Courier New, monospace',
                                    lineHeight: '1.2',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere'
                                }}>
                                    {dailyStats?.spreadHigh
                                        ? formatWithPrecision(dailyStats.spreadHigh, primarySymbol)
                                        : (percentileBands?.p75 ? formatWithPrecision(percentileBands.p75, primarySymbol) : '-')}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: '1', minWidth: '0', textAlign: 'right' }}>
                                <Typography sx={{ color: 'rgba(239, 68, 68, 0.9)', fontSize: '11px', lineHeight: '1', fontWeight: '800' }}>L</Typography>
                                <Typography sx={{
                                    color: 'rgba(239, 68, 68, 1)',
                                    fontSize: '12px',
                                    fontWeight: '800',
                                    fontFamily: 'Courier New, monospace',
                                    lineHeight: '1.2',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere'
                                }}>
                                    {dailyStats?.spreadLow
                                        ? formatWithPrecision(dailyStats.spreadLow, primarySymbol)
                                        : (percentileBands?.p25 ? formatWithPrecision(percentileBands.p25, primarySymbol) : '-')}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Percentile Metric */}
                    <Box sx={{
                        backgroundColor: 'rgba(251, 146, 60, 0.05)',
                        border: '1px solid rgba(251, 146, 60, 0.35)',
                        borderRadius: '4px',
                        padding: '5px 7px',
                        textAlign: 'center',
                        overflow: 'hidden',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                        height: '65px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                            <Typography sx={{
                                color: 'rgba(156, 163, 175, 0.7)',
                                fontSize: '8px',
                                fontWeight: '600',
                                letterSpacing: '0.5px',
                                marginBottom: '3px',
                                lineHeight: '1.2',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere'
                            }}>
                                PERCENTILE
                            </Typography>
                            <Typography sx={{
                                color: '#f59e0b',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                fontFamily: 'Courier New, monospace',
                                lineHeight: '1.1',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere'
                            }}>
                                {keyMetrics.percentile !== null
                                    ? `${keyMetrics.percentile.toFixed(0)}%`
                                    : '-'}
                            </Typography>
                        </Box>
                        {/* Range: P25 - P75 */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingTop: '4px', borderTop: '1px solid rgba(251, 146, 60, 0.15)', width: '100%' }}>
                            <Box sx={{ flex: '1', minWidth: '0' }}>
                                <Typography sx={{ color: 'rgba(156, 163, 175, 0.6)', fontSize: '10px', lineHeight: '1', fontWeight: '700' }}>P25</Typography>
                                <Typography sx={{
                                    color: 'rgba(251, 146, 60, 0.8)',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    fontFamily: 'Courier New, monospace',
                                    lineHeight: '1.2',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere'
                                }}>
                                    25%
                                </Typography>
                            </Box>
                            <Box sx={{ flex: '1', minWidth: '0', textAlign: 'right' }}>
                                <Typography sx={{ color: 'rgba(156, 163, 175, 0.6)', fontSize: '10px', lineHeight: '1', fontWeight: '700' }}>P75</Typography>
                                <Typography sx={{
                                    color: 'rgba(251, 146, 60, 0.8)',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    fontFamily: 'Courier New, monospace',
                                    lineHeight: '1.2',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere'
                                }}>
                                    75%
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Z-Score Metric */}
                    <Box sx={{
                        backgroundColor: 'rgba(139, 92, 246, 0.05)',
                        border: '1px solid rgba(139, 92, 246, 0.35)',
                        borderRadius: '4px',
                        padding: '5px 7px',
                        textAlign: 'center',
                        overflow: 'hidden',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                        height: '65px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                            <Typography sx={{
                                color: 'rgba(156, 163, 175, 0.7)',
                                fontSize: '8px',
                                fontWeight: '600',
                                letterSpacing: '0.5px',
                                marginBottom: '3px',
                                lineHeight: '1.2',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere'
                            }}>
                                Z-SCORE
                            </Typography>
                            <Typography sx={{
                                color: '#8b5cf6',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                fontFamily: 'Courier New, monospace',
                                lineHeight: '1.1',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere'
                            }}>
                                {keyMetrics.zScore !== null
                                    ? keyMetrics.zScore.toFixed(2)
                                    : '-'}
                            </Typography>
                        </Box>
                        {/* Range: Daily Low/High - PHASE 2: Use dailyStats (actual daily extremes) */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingTop: '4px', borderTop: '1px solid rgba(139, 92, 246, 0.15)', width: '100%' }}>
                            <Box sx={{ flex: '1', minWidth: '0' }}>
                                <Typography sx={{ color: 'rgba(156, 163, 175, 0.6)', fontSize: '10px', lineHeight: '1', fontWeight: '700' }}>LOW</Typography>
                                <Typography sx={{
                                    color: 'rgba(139, 92, 246, 0.8)',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    fontFamily: 'Courier New, monospace',
                                    lineHeight: '1.2',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere'
                                }}>
                                    {dailyStats?.zScoreLow !== undefined
                                        ? dailyStats.zScoreLow.toFixed(2)
                                        : '-2.00'}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: '1', minWidth: '0', textAlign: 'right' }}>
                                <Typography sx={{ color: 'rgba(156, 163, 175, 0.6)', fontSize: '10px', lineHeight: '1', fontWeight: '700' }}>HIGH</Typography>
                                <Typography sx={{
                                    color: 'rgba(139, 92, 246, 0.8)',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    fontFamily: 'Courier New, monospace',
                                    lineHeight: '1.2',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere'
                                }}>
                                    {dailyStats?.zScoreHigh !== undefined
                                        ? dailyStats.zScoreHigh.toFixed(2)
                                        : '+2.00'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Volatility Metric */}
                    <Box sx={{
                        backgroundColor: 'rgba(239, 68, 68, 0.05)',
                        border: '1px solid rgba(239, 68, 68, 0.35)',
                        borderRadius: '4px',
                        padding: '5px 7px',
                        textAlign: 'center',
                        overflow: 'hidden',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                        height: '65px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                            <Typography sx={{
                                color: 'rgba(156, 163, 175, 0.7)',
                                fontSize: '8px',
                                fontWeight: '600',
                                letterSpacing: '0.5px',
                                marginBottom: '3px',
                                lineHeight: '1.2',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere'
                            }}>
                                VOLATILITY (<span style={{ fontSize: '10px', fontWeight: '800', color: 'rgba(239, 68, 68, 0.9)' }}>σ</span>)
                            </Typography>
                            <Typography sx={{
                                color: '#ef4444',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                fontFamily: 'Courier New, monospace',
                                lineHeight: '1.1',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere'
                            }}>
                                {keyMetrics.volatility !== null
                                    ? keyMetrics.volatility.toFixed(2)
                                    : '-'}
                            </Typography>
                        </Box>
                        {/* IQR Range */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingTop: '4px', borderTop: '1px solid rgba(239, 68, 68, 0.15)', width: '100%' }}>
                            <Box sx={{ flex: '1', minWidth: '0' }}>
                                <Typography sx={{ color: 'rgba(156, 163, 175, 0.6)', fontSize: '10px', lineHeight: '1', fontWeight: '700' }}>IQR</Typography>
                                <Typography sx={{
                                    color: 'rgba(239, 68, 68, 0.8)',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    fontFamily: 'Courier New, monospace',
                                    lineHeight: '1.2',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere'
                                }}>
                                    {percentileBands?.p75 && percentileBands?.p25
                                        ? (percentileBands.p75 - percentileBands.p25).toFixed(2)
                                        : '-'}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: '1', minWidth: '0', textAlign: 'right' }}>
                                <Typography sx={{ color: 'rgba(156, 163, 175, 0.6)', fontSize: '10px', lineHeight: '1', fontWeight: '700' }}>1σ%</Typography>
                                <Typography sx={{
                                    color: 'rgba(239, 68, 68, 0.8)',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    fontFamily: 'Courier New, monospace',
                                    lineHeight: '1.2',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere'
                                }}>
                                    68%
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* ===================================================================
                    SECTION 2: ANALYTICS CHART (260px) - COMPACT
                    Chart comes IMMEDIATELY after header (not buried at bottom)
                    =================================================================== */}
                <Box sx={{ width: '100%' }}>
                    {isLoadingChart ? (
                        <Box sx={{
                            height: '260px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(17, 24, 39, 0.5)',
                            borderRadius: '8px',
                            border: '1px solid #262626'
                        }}>
                            <Typography sx={{ color: '#ffffff', fontSize: '14px' }}>
                                Loading chart data...
                            </Typography>
                        </Box>
                    ) : (
                        <StrategyMiniChart
                            data={chartData}
                            mode={chartMode}
                            onModeChange={setChartMode}
                            percentileBands={percentileBands}
                            dailyStats={dailyStats}
                            height={180}
                            currentSpread={marketData?.betaSpread}
                            currentBeta={marketData?.beta ?? (historicalBars.length > 0 ? historicalBars[historicalBars.length - 1]?.beta : null)}
                            lineColor="#ffffff"
                        />
                    )}
                </Box>

                {/* ===================================================================
                    SECTION 3: GRID STATUS (120px) - 22%
                    2-column layout: Performance | Position
                    Collapsed by default (space saving)
                    =================================================================== */}
                <Box>
                    {/* Grid Status Header (Collapsible) */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: '0 8px',
                        backgroundColor: '#141414',
                        borderRadius: '4px',
                        border: '1px solid #262626',
                        marginBottom: '4px',
                        height: '24px'
                    }} onClick={() => setGridStatusExpanded(!gridStatusExpanded)}>
                        <Typography sx={{
                            color: '#ffffff',
                            fontSize: '8px',
                            fontWeight: '700',
                            letterSpacing: '0.3px',
                            textTransform: 'uppercase',
                            lineHeight: '24px'
                        }}>
                            GRID STATUS
                        </Typography>
                        <ExpandMore
                            expand={gridStatusExpanded}
                            aria-expanded={gridStatusExpanded}
                            aria-label="show more"
                        >
                            <ExpandMoreIcon sx={{ color: '#ffffff', fontSize: '14px' }} />
                        </ExpandMore>
                    </Box>

                    {/* Grid Status Content (Collapsible) - ULTRA-DENSE 2-ROW LAYOUT */}
                    <Collapse in={gridStatusExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            {/* ROW 1: LEGS & ENTRY & P/L */}
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: '3px'
                            }}>
                                {/* Cell 1: Active Legs */}
                                <Box sx={{
                                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                                    border: '1px solid #262626',
                                    borderRadius: '3px',
                                    padding: '3px 4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}>
                                    <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '8px', marginRight: '4px' }}>
                                        Active: <span style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '11px', fontFamily: 'Courier New, monospace', fontWeight: '900' }}>{position?.activeLegs ?? '-'}</span>
                                        {' '}(<span style={{ color: '#10b981', fontSize: '9px', fontWeight: '700' }}>B:{position?.buySpreadLegs ?? '-'}</span>/<span style={{ color: '#ef4444', fontSize: '9px', fontWeight: '700' }}>S:{position?.sellSpreadLegs ?? '-'}</span>)
                                        {' '}Net: <span style={{
                                            color: position?.netExposure > 0 ? '#10b981' : position?.netExposure < 0 ? '#ef4444' : 'rgba(255, 255, 255, 0.9)',
                                            fontSize: '9px',
                                            fontWeight: '800'
                                        }}>{position?.netExposure?.toFixed(2) ?? '-'}</span>
                                    </Typography>
                                </Box>

                                {/* Cell 2: Next Entry Levels */}
                                <Box sx={{
                                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                                    border: '1px solid #262626',
                                    borderRadius: '3px',
                                    padding: '3px 4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}>
                                    <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '8px' }}>
                                        Buy@: <span style={{ color: '#ffffff', fontSize: '10px', fontFamily: 'Courier New, monospace', fontWeight: '900' }}>{formatWithPrecision(position?.buySpreadNextLegEntry, primarySymbol)}</span>
                                        {' '}Sell@: <span style={{ color: '#ef4444', fontSize: '10px', fontFamily: 'Courier New, monospace', fontWeight: '900' }}>{formatWithPrecision(position?.sellSpreadNextLegEntry, primarySymbol)}</span>
                                    </Typography>
                                </Box>

                                {/* Cell 3: P/L Targets */}
                                <Box sx={{
                                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                                    border: '1px solid #262626',
                                    borderRadius: '3px',
                                    padding: '3px 4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}>
                                    <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '8px' }}>
                                        TP: <span style={{
                                            color: position?.accumulativeTP >= 0 ? '#10b981' : '#ef4444',
                                            fontSize: '10px',
                                            fontFamily: 'Courier New, monospace',
                                            fontWeight: '900'
                                        }}>{position?.accumulativeTP !== undefined ? `${position.accumulativeTP >= 0 ? '+' : ''}$${Math.abs(position.accumulativeTP).toFixed(0)}` : '-'}</span>
                                        {' '}SL: <span style={{ color: '#ef4444', fontSize: '10px', fontFamily: 'Courier New, monospace', fontWeight: '900' }}>{position?.stopLoss !== undefined ? `-$${Math.abs(position.stopLoss).toFixed(0)}` : '-'}</span>
                                    </Typography>
                                </Box>
                            </Box>

                            {/* ROW 2: VOL & GRID LEVELS */}
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: '3px'
                            }}>
                                {/* Cell 1: Vol & LastTP */}
                                <Box sx={{
                                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                                    border: '1px solid #262626',
                                    borderRadius: '3px',
                                    padding: '3px 4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}>
                                    <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '8px' }}>
                                        Vol: <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '9px', fontFamily: 'Courier New, monospace', fontWeight: '700' }}>{position?.totalLotSize?.toFixed(2) ?? '-'}</span>
                                        {' '}LastTP: <span style={{ color: '#10b981', fontSize: '9px', fontFamily: 'Courier New, monospace', fontWeight: '700' }}>{formatWithPrecision(position?.lastLegTP, primarySymbol)}</span>
                                    </Typography>
                                </Box>

                                {/* Cell 2: Next Buy Grid */}
                                <Box sx={{
                                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                                    border: '1px solid #262626',
                                    borderRadius: '3px',
                                    padding: '3px 4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}>
                                    <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '8px' }}>
                                        NxtB/G@: <span style={{ color: '#ffffff', fontSize: '9px', fontFamily: 'Courier New, monospace', fontWeight: '800' }}>{formatWithPrecision(position?.buySpreadFirstLegEntry, primarySymbol)}</span>
                                    </Typography>
                                </Box>

                                {/* Cell 3: Next Sell Grid */}
                                <Box sx={{
                                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                                    border: '1px solid #262626',
                                    borderRadius: '3px',
                                    padding: '3px 4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}>
                                    <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '8px' }}>
                                        NxtS/G@: <span style={{ color: '#ef4444', fontSize: '9px', fontFamily: 'Courier New, monospace', fontWeight: '800' }}>{formatWithPrecision(position?.sellSpreadFirstLegEntry, primarySymbol)}</span>
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Collapse>
                </Box>

                {/* ===================================================================
                    CONTROL BAR (80px) - 14%
                    All action buttons at bottom + Config button
                    =================================================================== */}
                <Box>
                    {/* Controls Header (Collapsible) */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: '0 8px',
                        backgroundColor: '#141414',
                        borderRadius: '4px',
                        border: '1px solid #262626',
                        marginBottom: '4px',
                        height: '24px'
                    }} onClick={() => setControlsExpanded(!controlsExpanded)}>
                        <Typography sx={{
                            color: '#ffffff',
                            fontSize: '8px',
                            fontWeight: '700',
                            letterSpacing: '0.3px',
                            textTransform: 'uppercase',
                            lineHeight: '24px'
                        }}>
                            CONTROLS
                        </Typography>
                        <ExpandMore
                            expand={controlsExpanded}
                            aria-expanded={controlsExpanded}
                            aria-label="show controls"
                        >
                            <ExpandMoreIcon sx={{ color: '#ffffff', fontSize: '14px' }} />
                        </ExpandMore>
                    </Box>

                    {/* Controls Content (Collapsible) */}
                    <Collapse in={controlsExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{
                            backgroundColor: 'rgba(17, 24, 39, 0.8)',
                            border: '1px solid #262626',
                            borderRadius: '6px',
                            padding: '5px'
                        }}>
                            {/* Control Buttons Grid - 2-column responsive layout for better visibility */}
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '5px'
                            }}>
                        {/* Row 1: Buy Spread | Sell Spread - C4: Disabled when isDisabledByAdmin */}
                        <Tooltip title={isDisabledByAdmin ? disabledTooltip : ''} arrow placement="top">
                            <span style={{ display: 'contents' }}>
                                <button
                                    type="button"
                                    className="strategy-btn buy-spread"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (onBuySpread && !isDisabledByAdmin) onBuySpread(strategyId);
                                    }}
                                    disabled={!strategyId || isDisabledByAdmin}
                                    style={{
                                        fontSize: '10px',
                                        padding: '6px 9px',
                                        fontWeight: '700',
                                        opacity: isDisabledByAdmin ? 0.5 : 1,
                                        cursor: isDisabledByAdmin ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    BUY SPREAD
                                </button>
                            </span>
                        </Tooltip>
                        <Tooltip title={isDisabledByAdmin ? disabledTooltip : ''} arrow placement="top">
                            <span style={{ display: 'contents' }}>
                                <button
                                    type="button"
                                    className="strategy-btn sell-spread"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (onSellSpread && !isDisabledByAdmin) onSellSpread(strategyId);
                                    }}
                                    disabled={!strategyId || isDisabledByAdmin}
                                    style={{
                                        fontSize: '10px',
                                        padding: '6px 9px',
                                        fontWeight: '700',
                                        opacity: isDisabledByAdmin ? 0.5 : 1,
                                        cursor: isDisabledByAdmin ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    SELL SPREAD
                                </button>
                            </span>
                        </Tooltip>

                        {/* Row 2: Close Buy | Close Sell - C4: Disabled when isDisabledByAdmin */}
                        <Tooltip title={isDisabledByAdmin ? disabledTooltip : ''} arrow placement="top">
                            <span style={{ display: 'contents' }}>
                                <button
                                    type="button"
                                    className="strategy-btn close-buy"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (onCloseBuy && !isDisabledByAdmin) onCloseBuy(strategyId);
                                    }}
                                    disabled={!strategyId || isDisabledByAdmin}
                                    style={{
                                        fontSize: '10px',
                                        padding: '6px 9px',
                                        fontWeight: '700',
                                        opacity: isDisabledByAdmin ? 0.5 : 1,
                                        cursor: isDisabledByAdmin ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    CLOSE BUY
                                </button>
                            </span>
                        </Tooltip>
                        <Tooltip title={isDisabledByAdmin ? disabledTooltip : ''} arrow placement="top">
                            <span style={{ display: 'contents' }}>
                                <button
                                    type="button"
                                    className="strategy-btn close-sell"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (onCloseSell && !isDisabledByAdmin) onCloseSell(strategyId);
                                    }}
                                    disabled={!strategyId || isDisabledByAdmin}
                                    style={{
                                        fontSize: '10px',
                                        padding: '6px 9px',
                                        fontWeight: '700',
                                        opacity: isDisabledByAdmin ? 0.5 : 1,
                                        cursor: isDisabledByAdmin ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    CLOSE SELL
                                </button>
                            </span>
                        </Tooltip>

                        {/* Row 3: Close All | Config - C4: Disabled when isDisabledByAdmin */}
                        <Tooltip title={isDisabledByAdmin ? disabledTooltip : ''} arrow placement="top">
                            <span style={{ display: 'contents' }}>
                                <button
                                    type="button"
                                    className="strategy-btn close-all"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (onCloseAll && !isDisabledByAdmin) onCloseAll(strategyId);
                                    }}
                                    disabled={!strategyId || isDisabledByAdmin}
                                    style={{
                                        fontSize: '10px',
                                        padding: '6px 9px',
                                        fontWeight: '700',
                                        opacity: isDisabledByAdmin ? 0.5 : 1,
                                        cursor: isDisabledByAdmin ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    CLOSE ALL
                                </button>
                            </span>
                        </Tooltip>
                        <Tooltip title={isDisabledByAdmin ? disabledTooltip : ''} arrow placement="top">
                            <span style={{ display: 'contents' }}>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (!isDisabledByAdmin) setConfigDrawerOpen(true);
                                    }}
                                    disabled={isDisabledByAdmin}
                                    style={{
                                        background: isDisabledByAdmin
                                            ? 'rgba(107, 114, 128, 0.2)'
                                            : 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)',
                                        border: isDisabledByAdmin
                                            ? '1px solid rgba(107, 114, 128, 0.5)'
                                            : '1px solid rgba(139, 92, 246, 0.5)',
                                        borderRadius: '4px',
                                        color: isDisabledByAdmin ? '#6b7280' : '#8b5cf6',
                                        fontSize: '10px',
                                        fontWeight: '700',
                                        padding: '6px 9px',
                                        cursor: isDisabledByAdmin ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px',
                                        transition: 'all 0.2s ease',
                                        opacity: isDisabledByAdmin ? 0.5 : 1
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isDisabledByAdmin) {
                                            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(139, 92, 246, 0.2) 100%)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isDisabledByAdmin) {
                                            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)';
                                        }
                                    }}
                                >
                                    <SettingsIcon sx={{ fontSize: '11px' }} />
                                    CONFIG
                                </button>
                            </span>
                        </Tooltip>
                    </Box>

                    {/* START | STOP | RESET Buttons (3-button row) - C4: Disabled when isDisabledByAdmin */}
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '5px',
                        marginTop: '5px'
                    }}>
                        {/* START Button */}
                        <Tooltip title={isDisabledByAdmin ? disabledTooltip : ''} arrow placement="top">
                            <span style={{ display: 'contents' }}>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (!isDisabledByAdmin) {
                                            console.log('START strategy:', strategyId);
                                            // TODO: Implement onStartStrategy handler
                                        }
                                    }}
                                    disabled={!strategyId || isDisabledByAdmin}
                                    style={{
                                        background: isDisabledByAdmin
                                            ? 'rgba(107, 114, 128, 0.2)'
                                            : 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(34, 197, 94, 0.1) 100%)',
                                        border: isDisabledByAdmin
                                            ? '1px solid rgba(107, 114, 128, 0.5)'
                                            : '1px solid rgba(34, 197, 94, 0.5)',
                                        borderRadius: '4px',
                                        color: isDisabledByAdmin ? '#6b7280' : '#10b981',
                                        fontSize: '10px',
                                        fontWeight: '700',
                                        padding: '6px 9px',
                                        cursor: isDisabledByAdmin ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s ease',
                                        opacity: isDisabledByAdmin ? 0.5 : 1
                                    }}
                                    onMouseEnter={(e) => {
                                        if (strategyId && !isDisabledByAdmin) e.currentTarget.style.background = 'linear-gradient(135deg, rgba(34, 197, 94, 0.3) 0%, rgba(34, 197, 94, 0.2) 100%)';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isDisabledByAdmin) e.currentTarget.style.background = 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(34, 197, 94, 0.1) 100%)';
                                    }}
                                >
                                    START
                                </button>
                            </span>
                        </Tooltip>

                        {/* STOP Button */}
                        <Tooltip title={isDisabledByAdmin ? disabledTooltip : ''} arrow placement="top">
                            <span style={{ display: 'contents' }}>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (!isDisabledByAdmin) {
                                            console.log('STOP strategy:', strategyId);
                                            // TODO: Implement onStopStrategy handler
                                        }
                                    }}
                                    disabled={!strategyId || isDisabledByAdmin}
                                    style={{
                                        background: isDisabledByAdmin
                                            ? 'rgba(107, 114, 128, 0.2)'
                                            : 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.1) 100%)',
                                        border: isDisabledByAdmin
                                            ? '1px solid rgba(107, 114, 128, 0.5)'
                                            : '1px solid rgba(239, 68, 68, 0.5)',
                                        borderRadius: '4px',
                                        color: isDisabledByAdmin ? '#6b7280' : '#ef4444',
                                        fontSize: '10px',
                                        fontWeight: '700',
                                        padding: '6px 9px',
                                        cursor: isDisabledByAdmin ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s ease',
                                        opacity: isDisabledByAdmin ? 0.5 : 1
                                    }}
                                    onMouseEnter={(e) => {
                                        if (strategyId && !isDisabledByAdmin) e.currentTarget.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.3) 0%, rgba(239, 68, 68, 0.2) 100%)';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isDisabledByAdmin) e.currentTarget.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.1) 100%)';
                                    }}
                                >
                                    STOP
                                </button>
                            </span>
                        </Tooltip>

                        {/* RESET Button */}
                        <Tooltip title={isDisabledByAdmin ? disabledTooltip : ''} arrow placement="top">
                            <span style={{ display: 'contents' }}>
                                <button
                                    type="button"
                                    className="strategy-btn reset-strategy"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (onResetStrategy && !isDisabledByAdmin) onResetStrategy(strategyId);
                                    }}
                                    disabled={!strategyId || isDisabledByAdmin}
                                    style={{
                                        background: isDisabledByAdmin
                                            ? 'rgba(107, 114, 128, 0.2)'
                                            : 'linear-gradient(135deg, rgba(251, 146, 60, 0.2) 0%, rgba(251, 146, 60, 0.1) 100%)',
                                        border: isDisabledByAdmin
                                            ? '1px solid rgba(107, 114, 128, 0.5)'
                                            : '1px solid rgba(251, 146, 60, 0.5)',
                                        borderRadius: '4px',
                                        color: isDisabledByAdmin ? '#6b7280' : '#f59e0b',
                                        fontSize: '10px',
                                        fontWeight: '700',
                                        padding: '6px 9px',
                                        cursor: isDisabledByAdmin ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s ease',
                                        opacity: isDisabledByAdmin ? 0.5 : 1
                                    }}
                                    onMouseEnter={(e) => {
                                        if (strategyId && !isDisabledByAdmin) e.currentTarget.style.background = 'linear-gradient(135deg, rgba(251, 146, 60, 0.3) 0%, rgba(251, 146, 60, 0.2) 100%)';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isDisabledByAdmin) e.currentTarget.style.background = 'linear-gradient(135deg, rgba(251, 146, 60, 0.2) 0%, rgba(251, 146, 60, 0.1) 100%)';
                                    }}
                                >
                                    RESET
                                </button>
                            </span>
                        </Tooltip>
                    </Box>
                        </Box>
                    </Collapse>
                </Box>

                {/* ===================================================================
                    SECTION 4: PERCENTILE BANDS - NEW COMPONENT
                    Pure CSS horizontal bar chart visualization
                    Collapsed by default for clean layout
                    =================================================================== */}
                {percentileBands && (
                    <Box sx={{ marginTop: '8px' }}>
                        {/* Percentile Bands Header (Collapsible) */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            padding: '0 8px',
                            backgroundColor: '#141414',
                            borderRadius: '4px',
                            border: '1px solid #262626',
                            marginBottom: '4px',
                            height: '24px'
                        }} onClick={() => setPercentileBandsExpanded(!percentileBandsExpanded)}>
                            <Typography sx={{
                                color: '#ffffff',
                                fontSize: '8px',
                                fontWeight: '700',
                                letterSpacing: '0.3px',
                                textTransform: 'uppercase',
                                lineHeight: '24px'
                            }}>
                                PERCENTILE BANDS
                            </Typography>
                            <ExpandMore
                                expand={percentileBandsExpanded}
                                aria-expanded={percentileBandsExpanded}
                                aria-label="show percentile bands"
                            >
                                <ExpandMoreIcon sx={{ color: '#ffffff', fontSize: '14px' }} />
                            </ExpandMore>
                        </Box>

                        {/* Percentile Bands Content (Collapsible) */}
                        <Collapse in={percentileBandsExpanded} timeout="auto" unmountOnExit>
                            <PercentileBands
                                percentileBands={percentileBands}
                                currentValue={keyMetrics.betaSpread}
                                primarySymbol={primarySymbol}
                            />
                        </Collapse>
                    </Box>
                )}
            </CardContent>

            {/* ===================================================================
                CONFIG DRAWER (Material-UI Drawer)
                Opens from right side (400px width)
                8 parameter fields + Apply button
                =================================================================== */}
            <Drawer
                anchor="right"
                open={configDrawerOpen}
                onClose={() => setConfigDrawerOpen(false)}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: '400px',
                        backgroundColor: 'rgba(17, 24, 39, 0.98)',
                        borderLeft: '1px solid #374151',
                        padding: '20px'
                    }
                }}
            >
                {/* Drawer Header */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                    paddingBottom: '16px',
                    borderBottom: '1px solid #374151'
                }}>
                    <Typography sx={{
                        color: '#ffffff',
                        fontSize: '18px',
                        fontWeight: '700',
                        letterSpacing: '0.5px'
                    }}>
                        STRATEGY CONFIG
                    </Typography>
                    <IconButton
                        onClick={() => setConfigDrawerOpen(false)}
                        sx={{ color: 'rgba(156, 163, 175, 0.8)' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Strategy Info */}
                <Box sx={{
                    backgroundColor: '#141414',
                    border: '1px solid #262626',
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '20px'
                }}>
                    <Typography sx={{
                        color: '#ffffff',
                        fontSize: '14px',
                        fontWeight: '600',
                        marginBottom: '4px'
                    }}>
                        {strategyName || `Strategy #${strategyId}`}
                    </Typography>
                    <Typography sx={{
                        color: 'rgba(156, 163, 175, 0.8)',
                        fontSize: '11px',
                        fontFamily: 'Courier New, monospace'
                    }}>
                        {marketData?.futuresSymbol || '-'} / {marketData?.spotSymbol || '-'}
                    </Typography>
                </Box>

                {/* Parameter Fields - Collapsible Sections */}
                <Box sx={{ marginBottom: '20px', maxHeight: 'calc(100vh - 280px)', overflowY: 'auto', paddingRight: '4px' }}>

                    {/* SECTION 1: Entry Settings */}
                    <Box sx={{ marginBottom: '12px' }}>
                        <Box
                            onClick={() => setConfigSectionsExpanded(prev => ({ ...prev, entry: !prev.entry }))}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                padding: '8px 10px',
                                backgroundColor: '#141414',
                                borderRadius: '4px',
                                border: '1px solid #262626',
                                marginBottom: '8px'
                            }}
                        >
                            <Typography sx={{ color: '#ffffff', fontSize: '11px', fontWeight: '700', letterSpacing: '0.4px' }}>
                                📊 ENTRY SETTINGS
                            </Typography>
                            <ExpandMore expand={configSectionsExpanded.entry}>
                                <ExpandMoreIcon sx={{ color: '#ffffff', fontSize: '14px' }} />
                            </ExpandMore>
                        </Box>
                        <Collapse in={configSectionsExpanded.entry}>
                            <Box sx={{ paddingLeft: '8px' }}>
                                {/* Grid Threshold */}
                                <Box sx={{ marginBottom: '10px' }}>
                                    <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '10px', marginBottom: '4px' }}>
                                        Grid Threshold
                                    </Typography>
                                    <input
                                        type="number"
                                        step="1"
                                        value={strategyParams.gridThreshold}
                                        onChange={(e) => handleParamChange('gridThreshold', e.target.value)}
                                        onWheel={(e) => e.target.blur()}
                                        style={{
                                            width: '100%',
                                            backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                            border: '1px solid #262626',
                                            borderRadius: '3px',
                                            color: '#ffffff',
                                            fontSize: '11px',
                                            fontFamily: 'Courier New, monospace',
                                            padding: '7px 9px'
                                        }}
                                    />
                                </Box>
                                {/* Trade Entry Mode */}
                                <Box sx={{ marginBottom: '10px' }}>
                                    <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '10px', marginBottom: '4px' }}>
                                        Trade Entry Mode
                                    </Typography>
                                    <select
                                        value={strategyParams.tradeEntryMode}
                                        onChange={(e) => handleParamChange('tradeEntryMode', e.target.value)}
                                        style={{
                                            width: '100%',
                                            backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                            border: '1px solid #262626',
                                            borderRadius: '3px',
                                            color: '#ffffff',
                                            fontSize: '11px',
                                            fontFamily: 'Courier New, monospace',
                                            padding: '7px 9px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="Percentile">Percentile</option>
                                        <option value="Z-Score">Z-Score</option>
                                        <option value="Beta">Beta</option>
                                    </select>
                                </Box>
                            </Box>
                        </Collapse>
                    </Box>

                    {/* SECTION 2: Grid Configuration */}
                    <Box sx={{ marginBottom: '12px' }}>
                        <Box
                            onClick={() => setConfigSectionsExpanded(prev => ({ ...prev, grid: !prev.grid }))}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                padding: '8px 10px',
                                backgroundColor: 'rgba(251, 146, 60, 0.08)',
                                borderRadius: '4px',
                                border: '1px solid rgba(251, 146, 60, 0.25)',
                                marginBottom: '8px'
                            }}
                        >
                            <Typography sx={{ color: '#f59e0b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.4px' }}>
                                📐 GRID CONFIGURATION
                            </Typography>
                            <ExpandMore expand={configSectionsExpanded.grid}>
                                <ExpandMoreIcon sx={{ color: '#f59e0b', fontSize: '14px' }} />
                            </ExpandMore>
                        </Box>
                        <Collapse in={configSectionsExpanded.grid}>
                            <Box sx={{ paddingLeft: '8px' }}>
                                {/* 3-column row: Max Grid Levels | Buy Grid | Sell Grid */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '10px' }}>
                                    <Box>
                                        <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '9px', marginBottom: '4px' }}>
                                            Max Levels
                                        </Typography>
                                        <input
                                            type="number"
                                            step="1"
                                            value={strategyParams.maxGridLevels}
                                            onChange={(e) => handleParamChange('maxGridLevels', e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            style={{
                                                width: '100%',
                                                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                                border: '1px solid rgba(251, 146, 60, 0.25)',
                                                borderRadius: '3px',
                                                color: '#ffffff',
                                                fontSize: '11px',
                                                fontFamily: 'Courier New, monospace',
                                                padding: '7px 6px'
                                            }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '9px', marginBottom: '4px' }}>
                                            Buy Grid
                                        </Typography>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={strategyParams.buyGrid}
                                            onChange={(e) => handleParamChange('buyGrid', e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            style={{
                                                width: '100%',
                                                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                                border: '1px solid rgba(251, 146, 60, 0.25)',
                                                borderRadius: '3px',
                                                color: '#ffffff',
                                                fontSize: '11px',
                                                fontFamily: 'Courier New, monospace',
                                                padding: '7px 6px'
                                            }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '9px', marginBottom: '4px' }}>
                                            Sell Grid
                                        </Typography>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={strategyParams.sellGrid}
                                            onChange={(e) => handleParamChange('sellGrid', e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            style={{
                                                width: '100%',
                                                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                                border: '1px solid rgba(251, 146, 60, 0.25)',
                                                borderRadius: '3px',
                                                color: '#ffffff',
                                                fontSize: '11px',
                                                fontFamily: 'Courier New, monospace',
                                                padding: '7px 6px'
                                            }}
                                        />
                                    </Box>
                                </Box>
                                {/* 2-column row: Buy Base Lot | Sell Base Lot */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '10px' }}>
                                    <Box>
                                        <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '9px', marginBottom: '4px' }}>
                                            Buy Base Lot
                                        </Typography>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={strategyParams.buyBaseLotSize}
                                            onChange={(e) => handleParamChange('buyBaseLotSize', e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            style={{
                                                width: '100%',
                                                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                                border: '1px solid rgba(251, 146, 60, 0.25)',
                                                borderRadius: '3px',
                                                color: '#ffffff',
                                                fontSize: '11px',
                                                fontFamily: 'Courier New, monospace',
                                                padding: '7px 9px'
                                            }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '9px', marginBottom: '4px' }}>
                                            Sell Base Lot
                                        </Typography>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={strategyParams.sellBaseLotSize}
                                            onChange={(e) => handleParamChange('sellBaseLotSize', e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            style={{
                                                width: '100%',
                                                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                                border: '1px solid rgba(251, 146, 60, 0.25)',
                                                borderRadius: '3px',
                                                color: '#ffffff',
                                                fontSize: '11px',
                                                fontFamily: 'Courier New, monospace',
                                                padding: '7px 9px'
                                            }}
                                        />
                                    </Box>
                                </Box>
                                {/* 2-column row: Buy Lot Multiplier | Sell Lot Multiplier */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '10px' }}>
                                    <Box>
                                        <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '9px', marginBottom: '4px' }}>
                                            Buy Lot Multi
                                        </Typography>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={strategyParams.buyLotSizeMultiplier}
                                            onChange={(e) => handleParamChange('buyLotSizeMultiplier', e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            style={{
                                                width: '100%',
                                                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                                border: '1px solid rgba(251, 146, 60, 0.25)',
                                                borderRadius: '3px',
                                                color: '#ffffff',
                                                fontSize: '11px',
                                                fontFamily: 'Courier New, monospace',
                                                padding: '7px 9px'
                                            }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '9px', marginBottom: '4px' }}>
                                            Sell Lot Multi
                                        </Typography>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={strategyParams.sellLotSizeMultiplier}
                                            onChange={(e) => handleParamChange('sellLotSizeMultiplier', e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            style={{
                                                width: '100%',
                                                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                                border: '1px solid rgba(251, 146, 60, 0.25)',
                                                borderRadius: '3px',
                                                color: '#ffffff',
                                                fontSize: '11px',
                                                fontFamily: 'Courier New, monospace',
                                                padding: '7px 9px'
                                            }}
                                        />
                                    </Box>
                                </Box>
                                {/* Max Lot Allowed */}
                                <Box sx={{ marginBottom: '10px' }}>
                                    <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '10px', marginBottom: '4px' }}>
                                        Max Lot Allowed
                                    </Typography>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={strategyParams.maxLotAllowed}
                                        onChange={(e) => handleParamChange('maxLotAllowed', e.target.value)}
                                        onWheel={(e) => e.target.blur()}
                                        style={{
                                            width: '100%',
                                            backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                            border: '1px solid rgba(251, 146, 60, 0.25)',
                                            borderRadius: '3px',
                                            color: '#ffffff',
                                            fontSize: '11px',
                                            fontFamily: 'Courier New, monospace',
                                            padding: '7px 9px'
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Collapse>
                    </Box>

                    {/* SECTION 3: Take Profit Settings */}
                    <Box sx={{ marginBottom: '12px' }}>
                        <Box
                            onClick={() => setConfigSectionsExpanded(prev => ({ ...prev, takeProfit: !prev.takeProfit }))}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                padding: '8px 10px',
                                backgroundColor: 'rgba(34, 197, 94, 0.08)',
                                borderRadius: '4px',
                                border: '1px solid rgba(34, 197, 94, 0.25)',
                                marginBottom: '8px'
                            }}
                        >
                            <Typography sx={{ color: '#10b981', fontSize: '11px', fontWeight: '700', letterSpacing: '0.4px' }}>
                                🎯 TAKE PROFIT SETTINGS
                            </Typography>
                            <ExpandMore expand={configSectionsExpanded.takeProfit}>
                                <ExpandMoreIcon sx={{ color: '#10b981', fontSize: '14px' }} />
                            </ExpandMore>
                        </Box>
                        <Collapse in={configSectionsExpanded.takeProfit}>
                            <Box sx={{ paddingLeft: '8px' }}>
                                {/* 2-column row: TP Grid Enabled (checkbox) | TP Mode (dropdown) */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px', marginBottom: '10px' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <input
                                            type="checkbox"
                                            checked={strategyParams.tpGridEnabled}
                                            onChange={(e) => handleParamChange('tpGridEnabled', e.target.checked)}
                                            style={{ cursor: 'pointer', width: '14px', height: '14px' }}
                                        />
                                        <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '9px' }}>
                                            TP Grid
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <select
                                            value={strategyParams.tpMode}
                                            onChange={(e) => handleParamChange('tpMode', e.target.value)}
                                            disabled={!strategyParams.tpGridEnabled}
                                            style={{
                                                width: '100%',
                                                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                                border: '1px solid rgba(34, 197, 94, 0.25)',
                                                borderRadius: '3px',
                                                color: '#ffffff',
                                                fontSize: '10px',
                                                fontFamily: 'Courier New, monospace',
                                                padding: '7px 6px',
                                                cursor: 'pointer',
                                                opacity: strategyParams.tpGridEnabled ? 1 : 0.5
                                            }}
                                        >
                                            <option value="Averaging Sigmoid">Averaging Sigmoid</option>
                                            <option value="Linear">Linear</option>
                                            <option value="Exponential">Exponential</option>
                                        </select>
                                    </Box>
                                </Box>
                                {/* TP Type Dropdown (Pip-based / ATR-based) */}
                                <Box sx={{ marginBottom: '10px' }}>
                                    <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '10px', marginBottom: '4px' }}>
                                        TP Type
                                    </Typography>
                                    <select
                                        value={strategyParams.tpType}
                                        onChange={(e) => handleParamChange('tpType', e.target.value)}
                                        style={{
                                            width: '100%',
                                            backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                            border: '1px solid rgba(34, 197, 94, 0.25)',
                                            borderRadius: '3px',
                                            color: '#ffffff',
                                            fontSize: '11px',
                                            fontFamily: 'Courier New, monospace',
                                            padding: '7px 9px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="Pip-based">Pip-based</option>
                                        <option value="ATR-based">ATR-based</option>
                                    </select>
                                </Box>
                                {/* TP Grid Type */}
                                <Box sx={{ marginBottom: '10px' }}>
                                    <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '10px', marginBottom: '4px' }}>
                                        TP Grid Type
                                    </Typography>
                                    <input
                                        type="number"
                                        step="1"
                                        value={strategyParams.tpGridType}
                                        onChange={(e) => handleParamChange('tpGridType', e.target.value)}
                                        onWheel={(e) => e.target.blur()}
                                        disabled={!strategyParams.tpGridEnabled}
                                        style={{
                                            width: '100%',
                                            backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                            border: '1px solid rgba(34, 197, 94, 0.25)',
                                            borderRadius: '3px',
                                            color: '#ffffff',
                                            fontSize: '11px',
                                            fontFamily: 'Courier New, monospace',
                                            padding: '7px 9px',
                                            opacity: strategyParams.tpGridEnabled ? 1 : 0.5
                                        }}
                                    />
                                </Box>
                                {/* 2-column row: Buy Leg TP | Sell Leg TP */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '10px' }}>
                                    <Box>
                                        <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '9px', marginBottom: '4px' }}>
                                            Buy Leg TP
                                        </Typography>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={strategyParams.buyLegTP}
                                            onChange={(e) => handleParamChange('buyLegTP', e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            style={{
                                                width: '100%',
                                                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                                border: '1px solid rgba(34, 197, 94, 0.25)',
                                                borderRadius: '3px',
                                                color: '#ffffff',
                                                fontSize: '11px',
                                                fontFamily: 'Courier New, monospace',
                                                padding: '7px 9px'
                                            }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '9px', marginBottom: '4px' }}>
                                            Sell Leg TP
                                        </Typography>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={strategyParams.sellLegTP}
                                            onChange={(e) => handleParamChange('sellLegTP', e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            style={{
                                                width: '100%',
                                                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                                border: '1px solid rgba(34, 197, 94, 0.25)',
                                                borderRadius: '3px',
                                                color: '#ffffff',
                                                fontSize: '11px',
                                                fontFamily: 'Courier New, monospace',
                                                padding: '7px 9px'
                                            }}
                                        />
                                    </Box>
                                </Box>
                                {/* 2-column row: Buy Leg DL | Sell Leg DL */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '10px' }}>
                                    <Box>
                                        <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '9px', marginBottom: '4px' }}>
                                            Buy Leg DL
                                        </Typography>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={strategyParams.buyLegDL}
                                            onChange={(e) => handleParamChange('buyLegDL', e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            style={{
                                                width: '100%',
                                                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                                border: '1px solid rgba(34, 197, 94, 0.25)',
                                                borderRadius: '3px',
                                                color: '#ffffff',
                                                fontSize: '11px',
                                                fontFamily: 'Courier New, monospace',
                                                padding: '7px 9px'
                                            }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '9px', marginBottom: '4px' }}>
                                            Sell Leg DL
                                        </Typography>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={strategyParams.sellLegDL}
                                            onChange={(e) => handleParamChange('sellLegDL', e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            style={{
                                                width: '100%',
                                                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                                border: '1px solid rgba(34, 197, 94, 0.25)',
                                                borderRadius: '3px',
                                                color: '#ffffff',
                                                fontSize: '11px',
                                                fontFamily: 'Courier New, monospace',
                                                padding: '7px 9px'
                                            }}
                                        />
                                    </Box>
                                </Box>
                                {/* Accumulative TP */}
                                <Box sx={{ marginBottom: '10px' }}>
                                    <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '10px', marginBottom: '4px' }}>
                                        Accumulative TP
                                    </Typography>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={strategyParams.accumulativeTP}
                                        onChange={(e) => handleParamChange('accumulativeTP', e.target.value)}
                                        onWheel={(e) => e.target.blur()}
                                        style={{
                                            width: '100%',
                                            backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                            border: '1px solid rgba(34, 197, 94, 0.25)',
                                            borderRadius: '3px',
                                            color: '#ffffff',
                                            fontSize: '11px',
                                            fontFamily: 'Courier New, monospace',
                                            padding: '7px 9px'
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Collapse>
                    </Box>

                    {/* SECTION 4: Risk Management */}
                    <Box sx={{ marginBottom: '12px' }}>
                        <Box
                            onClick={() => setConfigSectionsExpanded(prev => ({ ...prev, risk: !prev.risk }))}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                padding: '8px 10px',
                                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                borderRadius: '4px',
                                border: '1px solid rgba(239, 68, 68, 0.25)',
                                marginBottom: '8px'
                            }}
                        >
                            <Typography sx={{ color: '#ef4444', fontSize: '11px', fontWeight: '700', letterSpacing: '0.4px' }}>
                                🛡️ RISK MANAGEMENT
                            </Typography>
                            <ExpandMore expand={configSectionsExpanded.risk}>
                                <ExpandMoreIcon sx={{ color: '#ef4444', fontSize: '14px' }} />
                            </ExpandMore>
                        </Box>
                        <Collapse in={configSectionsExpanded.risk}>
                            <Box sx={{ paddingLeft: '8px' }}>
                                {/* 2-column row: Stop Loss Enabled (checkbox) | Stop Loss Pips */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px', marginBottom: '10px' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <input
                                            type="checkbox"
                                            checked={strategyParams.stopLossEnabled}
                                            onChange={(e) => handleParamChange('stopLossEnabled', e.target.checked)}
                                            style={{ cursor: 'pointer', width: '14px', height: '14px' }}
                                        />
                                        <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '9px' }}>
                                            SL Enable
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <input
                                            type="number"
                                            step="1"
                                            placeholder="Pips"
                                            value={strategyParams.stopLossPips}
                                            onChange={(e) => handleParamChange('stopLossPips', e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            disabled={!strategyParams.stopLossEnabled}
                                            style={{
                                                width: '100%',
                                                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                                border: '1px solid rgba(239, 68, 68, 0.25)',
                                                borderRadius: '3px',
                                                color: '#ffffff',
                                                fontSize: '11px',
                                                fontFamily: 'Courier New, monospace',
                                                padding: '7px 9px',
                                                opacity: strategyParams.stopLossEnabled ? 1 : 0.5
                                            }}
                                        />
                                    </Box>
                                </Box>
                                {/* Accumulative SL */}
                                <Box sx={{ marginBottom: '10px' }}>
                                    <Typography sx={{ color: 'rgba(156, 163, 175, 0.75)', fontSize: '10px', marginBottom: '4px' }}>
                                        Accumulative SL
                                    </Typography>
                                    <input
                                        type="number"
                                        step="1"
                                        value={strategyParams.accumulativeSL}
                                        onChange={(e) => handleParamChange('accumulativeSL', e.target.value)}
                                        onWheel={(e) => e.target.blur()}
                                        style={{
                                            width: '100%',
                                            backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                            border: '1px solid rgba(239, 68, 68, 0.25)',
                                            borderRadius: '3px',
                                            color: '#ffffff',
                                            fontSize: '11px',
                                            fontFamily: 'Courier New, monospace',
                                            padding: '7px 9px'
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Collapse>
                    </Box>
                </Box>

                {/* Action Buttons (Sticky Bottom) - RESET | SAVE */}
                <Box sx={{
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: 'rgba(17, 24, 39, 0.98)',
                    paddingTop: '12px',
                    borderTop: '1px solid #374151'
                }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px' }}>
                        {/* RESET Button - A4: Restores original Admin-configured values */}
                        <button
                            onClick={() => {
                                // A4: Reset to original Admin-configured values (not empty)
                                if (originalParams) {
                                    setStrategyParams(originalParams);
                                    console.log('[StrategyCard] RESET: Restored original params:', originalParams);
                                } else {
                                    console.warn('[StrategyCard] RESET: No original params stored');
                                }
                            }}
                            disabled={!originalParams || isLoadingParams}
                            style={{
                                background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.2) 0%, rgba(251, 146, 60, 0.1) 100%)',
                                border: '1px solid rgba(251, 146, 60, 0.5)',
                                borderRadius: '6px',
                                color: '#f59e0b',
                                fontSize: '11px',
                                fontWeight: '700',
                                letterSpacing: '0.6px',
                                padding: '11px 8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 6px rgba(251, 146, 60, 0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(251, 146, 60, 0.3) 0%, rgba(251, 146, 60, 0.2) 100%)';
                                e.currentTarget.style.boxShadow = '0 4px 10px rgba(251, 146, 60, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(251, 146, 60, 0.2) 0%, rgba(251, 146, 60, 0.1) 100%)';
                                e.currentTarget.style.boxShadow = '0 2px 6px rgba(251, 146, 60, 0.25)';
                            }}
                        >
                            🔄 RESET
                        </button>

                        {/* SAVE Button - A5: Persists changes to backend */}
                        <button
                            onClick={handleApply}
                            disabled={isSavingParams || isLoadingParams}
                            style={{
                                background: isSavingParams ? '#333' : '#1a1a1a',
                                border: '2px solid #dc2626',
                                borderRadius: '4px',
                                color: isSavingParams ? '#999' : '#dc2626',
                                fontSize: '11px',
                                fontWeight: '900',
                                letterSpacing: '1px',
                                padding: '11px 8px',
                                cursor: isSavingParams ? 'wait' : 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 0 8px rgba(220, 38, 38, 0.4)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                opacity: (isSavingParams || isLoadingParams) ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => {
                                if (!isSavingParams) {
                                    e.currentTarget.style.background = '#262626';
                                    e.currentTarget.style.boxShadow = '0 0 16px rgba(220, 38, 38, 0.6)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isSavingParams) {
                                    e.currentTarget.style.background = '#1a1a1a';
                                    e.currentTarget.style.boxShadow = '0 0 8px rgba(220, 38, 38, 0.4)';
                                }
                            }}
                        >
                            {isSavingParams ? '⏳ SAVING...' : '💾 SAVE PARAMETERS'}
                        </button>
                    </Box>

                    {/* Save feedback message */}
                    {saveMessage && (
                        <Box sx={{
                            padding: '8px 12px',
                            marginTop: '8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600,
                            textAlign: 'center',
                            backgroundColor: saveMessage.type === 'success' ? 'rgba(34, 197, 94, 0.15)' :
                                           saveMessage.type === 'warning' ? 'rgba(251, 146, 60, 0.15)' :
                                           saveMessage.type === 'error' ? 'rgba(239, 68, 68, 0.15)' :
                                           'rgba(107, 114, 128, 0.15)',
                            color: saveMessage.type === 'success' ? '#22c55e' :
                                   saveMessage.type === 'warning' ? '#f59e0b' :
                                   saveMessage.type === 'error' ? '#ef4444' :
                                   '#9ca3af',
                            border: `1px solid ${
                                saveMessage.type === 'success' ? 'rgba(34, 197, 94, 0.3)' :
                                saveMessage.type === 'warning' ? 'rgba(251, 146, 60, 0.3)' :
                                saveMessage.type === 'error' ? 'rgba(239, 68, 68, 0.3)' :
                                'rgba(107, 114, 128, 0.3)'
                            }`
                        }}>
                            {saveMessage.text}
                        </Box>
                    )}
                </Box>
            </Drawer>
        </Card>
    );
};

// Memoize component to prevent unnecessary re-renders causing flickering
// Only re-render when strategyData actually changes (not just reference)
export default React.memo(StrategyCard, (prevProps, nextProps) => {
    // Custom comparison: Deep compare strategyData to prevent flickering
    const prevData = prevProps.strategyData;
    const nextData = nextProps.strategyData;

    // If both are null/undefined, no change
    if (!prevData && !nextData) return true;
    if (!prevData || !nextData) return false;

    // Compare critical fields that affect display
    const prevMarket = prevData.marketData || {};
    const nextMarket = nextData.marketData || {};

    const prevPerf = prevData.performance || {};
    const nextPerf = nextData.performance || {};

    const prevPos = prevData.position || {};
    const nextPos = nextData.position || {};

    // Check if any display-critical values changed
    const marketChanged = (
        prevMarket.betaSpread !== nextMarket.betaSpread ||
        prevMarket.beta !== nextMarket.beta ||
        prevMarket.zScore !== nextMarket.zScore ||
        prevMarket.upperBand !== nextMarket.upperBand ||
        prevMarket.lowerBand !== nextMarket.lowerBand ||
        prevMarket.session !== nextMarket.session
    );

    const perfChanged = (
        prevPerf.pnl !== nextPerf.pnl ||
        prevPerf.winRate !== nextPerf.winRate
    );

    const posChanged = (
        prevPos.volume !== nextPos.volume ||
        prevPos.entryPrice !== nextPos.entryPrice
    );

    // Other props that should trigger re-render
    const otherPropsChanged = (
        prevProps.strategyId !== nextProps.strategyId ||
        prevProps.strategyName !== nextProps.strategyName ||
        prevProps.token !== nextProps.token
    );

    // Return true if NO changes (skip re-render), false if changed (allow re-render)
    return !marketChanged && !perfChanged && !posChanged && !otherPropsChanged;
});
